import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';

/**
 * IPI — Índice de Proteção Institucional
 * Widget premium para o Dashboard e Visão Executiva.
 *
 * Faixas:
 *  90-100 Excelente (verde)
 *  75-89  Atenção (amarelo)
 *  60-74  Risco Moderado (laranja)
 *  0-59   Crítico (vermelho)
 */
export default function IPIWidget({ compact = false }) {
  const { api } = useAuth();
  const [ipi, setIpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/dashboard/ipi')
      .then((res) => { if (!cancelled) setIpi(res.data); })
      .catch(() => { if (!cancelled) setIpi(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm animate-pulse h-48" data-testid="ipi-widget-loading" />
    );
  }

  if (!ipi) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="ipi-widget-error">
        <p className="text-slate-500 text-sm">Não foi possível calcular o IPI agora.</p>
      </div>
    );
  }

  const colorMap = {
    green:  { ring: 'stroke-emerald-500', bg: 'from-emerald-500 to-teal-600',  text: 'text-emerald-700', soft: 'bg-emerald-50', border: 'border-emerald-200', icon: ShieldCheck },
    yellow: { ring: 'stroke-amber-500',   bg: 'from-amber-500 to-orange-500',  text: 'text-amber-700',   soft: 'bg-amber-50',   border: 'border-amber-200',   icon: Shield      },
    orange: { ring: 'stroke-orange-500',  bg: 'from-orange-500 to-red-500',    text: 'text-orange-700',  soft: 'bg-orange-50',  border: 'border-orange-200',  icon: ShieldAlert },
    red:    { ring: 'stroke-red-600',     bg: 'from-red-600 to-rose-700',      text: 'text-red-700',     soft: 'bg-red-50',     border: 'border-red-300',     icon: AlertTriangle },
    gray:   { ring: 'stroke-slate-400',   bg: 'from-slate-500 to-slate-700',   text: 'text-slate-600',   soft: 'bg-slate-50',   border: 'border-slate-200',   icon: Shield      },
  };
  const c = colorMap[ipi.color] || colorMap.gray;
  const Icon = c.icon;
  const score = Math.max(0, Math.min(100, ipi.ipi || 0));
  const circumference = 2 * Math.PI * 70;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className={`rounded-2xl border-2 ${c.border} bg-gradient-to-br from-white to-slate-50 p-6 md:p-8 shadow-lg`} data-testid="ipi-widget">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Índice de Proteção Institucional</p>
          <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">IPI</h3>
        </div>
        <div className={`px-3 py-1 rounded-full ${c.soft} ${c.text} text-xs font-bold uppercase tracking-wider flex items-center gap-1.5`} data-testid="ipi-band-label">
          <Icon className="w-3.5 h-3.5" />
          {ipi.label}
        </div>
      </div>

      <div className={`flex ${compact ? 'flex-row gap-6' : 'flex-col md:flex-row gap-8'} items-center`}>
        {/* Circular gauge */}
        <div className="relative w-44 h-44 md:w-52 md:h-52 shrink-0" data-testid="ipi-gauge">
          <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
            <circle cx="80" cy="80" r="70" className="fill-none stroke-slate-100" strokeWidth="14" />
            <circle
              cx="80" cy="80" r="70"
              className={`fill-none ${c.ring} transition-all duration-1000 ease-out`}
              strokeWidth="14" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-5xl md:text-6xl font-extrabold ${c.text}`} data-testid="ipi-score">{score.toFixed(0)}</span>
            <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">/ 100</span>
          </div>
        </div>

        {/* Components A/B/C/D + narrative */}
        <div className="flex-1 w-full min-w-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <ComponentBar label="No prazo" value={ipi.components?.A_on_time_pct ?? 0} testid="ipi-component-A" />
            <ComponentBar label="Com evidência" value={ipi.components?.B_with_evidence_pct ?? 0} testid="ipi-component-B" />
            <ComponentBar label="Encerrados" value={ipi.components?.C_closed_with_dossier_pct ?? 0} testid="ipi-component-C" />
            <ComponentBar label="Com responsável" value={ipi.components?.D_with_responsible_pct ?? 0} testid="ipi-component-D" />
          </div>
          {!compact && (
            <p className="text-sm md:text-[15px] text-slate-700 leading-relaxed border-l-4 border-slate-300 pl-4 italic" data-testid="ipi-narrative">
              {ipi.narrative}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ComponentBar({ label, value, testid }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <span className="text-sm font-extrabold text-slate-900">{v.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-slate-700 to-slate-900 rounded-full transition-all duration-700"
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}
